// ─── What: axis layout helpers ────────────────────────────────────────────────

import * as d3 from 'd3';
import { fmtProbPct } from './chartFormat';
import type { AxisFit } from './chartYDomain';

export interface Margin {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

export type AxisSvg = d3.Selection<
    SVGSVGElement | null,
    unknown,
    null,
    undefined
>;

export interface PercentAxisOpts {
    /** Tick positions in probability units. */
    ticks: number[];
    /** Scale from probability units to pixels. */
    y: (v: number) => number;
    x1: number;
    x2: number;
    labelSide: 'left' | 'right';
    gridColor: string;
    textColor: string;
    fontSize: string;
    dashArray?: string;
    /** Decimal places for the labels; 0 unless the step is finer than a point. */
    decimals?: number;
}

export interface AxisFitButtonOpts {
    x: number;
    y: number;
    fit: AxisFit;
    accentColor: string;
    mutedColor: string;
    onToggle: () => void;
}

const FIT_GLYPH = 22;
const FIT_HIT = 32;
/** The glyph paths are drawn in a 15-unit box and scaled to FIT_GLYPH. */
const GLYPH_UNITS = 15;

// ─── How it works ─────────────────────────────────────────────────────────────

/** Swap gutters so price tick labels render on the right edge (TradingView style). */
export function rightAxisMargin(base: Margin): Margin {
    return { ...base, left: 12, right: 48 };
}

/** Clamp a floating axis label position so it stays fully within [min, max]. */
export function clampLabelPos(
    pos: number,
    min: number,
    max: number,
    half: number
): number {
    return Math.max(min + half, Math.min(max - half, pos));
}

/** Draw percentage gridlines with labels in the gutter on one side. */
export function drawPercentAxis(svg: AxisSvg, opts: PercentAxisOpts): void {
    const { ticks, y, x1, x2, labelSide, gridColor, textColor, fontSize } =
        opts;
    const g = svg.append('g');

    const lines = g
        .selectAll('line')
        .data(ticks)
        .join('line')
        .attr('x1', x1)
        .attr('x2', x2)
        .attr('y1', d => y(d))
        .attr('y2', d => y(d))
        .attr('stroke', gridColor)
        .attr('stroke-width', 0.5);
    if (opts.dashArray) lines.attr('stroke-dasharray', opts.dashArray);

    g.selectAll('text')
        .data(ticks)
        .join('text')
        .attr('x', labelSide === 'right' ? x2 + 4 : x1 - 4)
        .attr('y', d => y(d) + 3)
        .attr('fill', textColor)
        .attr('font-size', fontSize)
        .attr('font-family', 'monospace')
        .attr('text-anchor', labelSide === 'right' ? 'start' : 'end')
        .text(d => fmtProbPct(d, opts.decimals ?? 0));
}

/** Draw the hairline that divides two stacked chart panels. */
export function drawPanelSeparator(
    svg: AxisSvg,
    x1: number,
    x2: number,
    y: number,
    color: string
): void {
    svg.append('line')
        .attr('x1', x1)
        .attr('x2', x2)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', color)
        .attr('stroke-width', 0.5);
}

/** Draw the gutter button that switches between a fitted and a 0–100% axis. */
export function drawAxisFitButton(svg: AxisSvg, opts: AxisFitButtonOpts): void {
    const { fit, accentColor, mutedColor, onToggle } = opts;
    // Clamp so a caller's headroom estimate can't push the glyph off-canvas.
    const x = Math.max(0, opts.x);
    const y = Math.max(0, opts.y);
    const scale = FIT_GLYPH / GLYPH_UNITS;
    const g = svg.append('g').style('cursor', 'pointer');

    g.append('title').text(
        fit === 'auto'
            ? 'Axis fits the data — click for 0–100%'
            : 'Axis pinned to 0–100% — click to fit the data'
    );

    const glyph = g
        .append('g')
        .attr('class', 'axis-fit-glyph')
        .attr('transform', `translate(${x},${y}) scale(${scale})`)
        .attr('stroke', fit === 'auto' ? accentColor : mutedColor)
        .attr('stroke-width', 1.1)
        .attr('stroke-linecap', 'round')
        .attr('fill', 'none');

    const w = GLYPH_UNITS;
    glyph
        .append('line')
        .attr('x1', 3.5)
        .attr('y1', 3)
        .attr('x2', w - 3.5)
        .attr('y2', 3);
    glyph
        .append('line')
        .attr('x1', 3.5)
        .attr('y1', w - 3)
        .attr('x2', w - 3.5)
        .attr('y2', w - 3);
    glyph.append('path').attr('d', 'M7.5 5.4 V9.6');
    glyph.append('path').attr('d', 'M5.9 6.9 L7.5 5.3 L9.1 6.9');
    glyph.append('path').attr('d', 'M5.9 8.1 L7.5 9.7 L9.1 8.1');

    const hit = g
        .append('rect')
        .attr('class', 'axis-fit-hit')
        .attr('x', Math.max(0, x + FIT_GLYPH / 2 - FIT_HIT / 2))
        .attr('y', Math.max(0, y + FIT_GLYPH / 2 - FIT_HIT / 2))
        .attr('width', FIT_HIT)
        .attr('height', FIT_HIT)
        .attr('fill', 'transparent');

    hit.on('click', (event: MouseEvent) => {
        event.stopPropagation();
        onToggle();
    });
    hit.on('mouseenter', () => glyph.attr('stroke-width', 1.6));
    hit.on('mouseleave', () => glyph.attr('stroke-width', 1.1));
}
